import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../../ui/components/Card";
import { Button } from "../../../../ui/components/Button";
import { Callout } from "../../../../ui/components/Callout";
import { Text } from "../../../../ui/components/Text";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { useAuth } from "../../../lib/auth/hooks";
import * as userApi from "../../../lib/api/users";
import {
  persistInviteContext,
  clearInviteToken,
} from "../../../lib/auth/invite-pending";
import { capturePendingExternalRedirect } from "../../../lib/auth/external-redirect";
import { getCertificationAppOrigin } from "../../../lib/auth/app-origins";
import { getApiErrorMessage } from "../../../lib/api/api-error";
import { useI18n } from "../../../lib/i18n";

export interface AcceptInvitePageProps {
  token?: string;
}

export function AcceptInvitePage({ token: tokenFromSearch }: AcceptInvitePageProps) {
  const search = { token: tokenFromSearch };
  const navigate = useNavigate();
  const { user, isAuthenticated, verifyEmailWithPendingInvite } = useAuth();
  const { t, tx } = useI18n();
  const inviteVerifyAttempted = React.useRef(false);
  const [loading, setLoading] = React.useState(true);
  const [preview, setPreview] = React.useState<userApi.InvitePreview | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const token = search.token?.trim();
    if (!token) {
      setError(t("account.acceptInvite.errorInvalid"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    void userApi
      .fetchInvitePreview(token)
      .then((result) => {
        setPreview(result);
        const intent =
          result.intent === "certification" ? "certification" : "membership";
        if (result.valid) {
          persistInviteContext(token, result.email, intent);
          if (intent === "certification") {
            capturePendingExternalRedirect(getCertificationAppOrigin());
          }
        } else {
          persistInviteContext(token, undefined, intent);
        }
        if (!result.valid) {
          if (result.expired) {
            clearInviteToken();
            setError(t("account.acceptInvite.errorExpired"));
          } else {
            clearInviteToken();
            setError(t("account.acceptInvite.errorUsed"));
          }
        }
      })
      .catch((e) => {
        clearInviteToken();
        setError(getApiErrorMessage(e, t("account.acceptInvite.errorLoad")));
      })
      .finally(() => setLoading(false));
  }, [search.token]);

  React.useEffect(() => {
    if (!preview?.valid || !isAuthenticated || !user?.email || !preview.email) {
      return;
    }
    if (user.email.toLowerCase() !== preview.email.toLowerCase()) {
      return;
    }
    if (!user.emailVerified) {
      if (!inviteVerifyAttempted.current) {
        inviteVerifyAttempted.current = true;
        void verifyEmailWithPendingInvite();
      }
      return;
    }
    if (!user.profileSetupCompleted) {
      navigate({ to: "/user-setup", search: { tab: "organization" }, replace: true });
    } else {
      navigate({ to: "/profile", replace: true });
    }
  }, [preview, isAuthenticated, user, navigate, verifyEmailWithPendingInvite]);

  const emailMismatch =
    preview?.valid &&
    isAuthenticated &&
    user?.email &&
    preview.email &&
    user.email.toLowerCase() !== preview.email.toLowerCase();

  const loginRedirect =
    typeof window !== "undefined"
      ? `${window.location.origin}/accept-invite?token=${encodeURIComponent(search.token ?? "")}`
      : "/accept-invite";

  return (
    <Card maxWidth={480} className={authText.userSetupCard}>
      <AuthBrand />
      <div className={authText.setupStepPanelForm}>
        <Text level="h3" weight="bold" style={{ margin: "0 0 8px" }}>
          {tx("account.acceptInvite.title")}
        </Text>

        {loading && (
          <Text level="body-medium" style={{ color: "#6B7280" }}>
            {tx("account.acceptInvite.loading")}
          </Text>
        )}

        {!loading && error && <Callout variant="error">{error}</Callout>}

        {!loading && preview?.valid && (
          <>
            <Text level="body-medium" style={{ margin: 0 }}>
              {tx("account.acceptInvite.invitedPrefix")}{" "}
              <strong>{preview.organizationName}</strong>
              {preview.email ? (
                <>
                  {" "}
                  {tx("account.acceptInvite.invitedAsPrefix")} <strong>{preview.email}</strong>
                </>
              ) : null}
              .
            </Text>

            {emailMismatch && (
              <Callout variant="error">
                {tx("account.acceptInvite.emailMismatchPrefix")} {user?.email}
                {tx("account.acceptInvite.emailMismatchMiddle")} {preview.email}{" "}
                {tx("account.acceptInvite.emailMismatchSuffix")}
              </Callout>
            )}

            {!isAuthenticated && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <Button
                  color="primary"
                  variant="solid"
                  fullWidth
                  size="lg"
                  onClick={() =>
                    navigate({
                      to: "/register",
                      search: { email: preview.email },
                    })
                  }
                >
                  {tx("account.acceptInvite.createAccount")}
                </Button>
                <Button
                  color="neutral"
                  variant="outline"
                  fullWidth
                  size="lg"
                  onClick={() =>
                    navigate({
                      to: "/login",
                      search: { redirect: loginRedirect },
                    })
                  }
                >
                  {tx("account.acceptInvite.logIn")}
                </Button>
              </div>
            )}

            {isAuthenticated && !emailMismatch && (
              <Text level="body-medium" style={{ color: "#6B7280" }}>
                {user?.emailVerified
                  ? t("account.acceptInvite.redirecting")
                  : t("account.acceptInvite.confirmingEmail")}
              </Text>
            )}
          </>
        )}

        {!loading && !error && !preview?.valid && (
          <p style={{ margin: 0 }}>
            <Link to="/login">{tx("account.acceptInvite.returnToLogin")}</Link>
          </p>
        )}
      </div>
    </Card>
  );
}
